import i18n from '@/utils/i18n';
import moment from 'moment';
import {
  AllowedShortcuts,
  KeyboardScope,
} from '@/platform/shortcuts/operator-shortcuts';
import {
  areTimeslotsEqual,
  ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP,
} from '@domain/fulfillment-settings/blocs/order-availability';
import {
  AvailableOrderFulfillmentFieldsFragment,
  FulfillmentName,
  FulfillmentType,
  NewOrder_OrderFieldsFragment,
} from '@/types';
import { formatCurrency } from '@/utils/money';
import { formatSummaryDate } from '@/utils/date';
import { ListOption } from '@/components/list-item';
import { SelectedFulfillmentType } from '@/contexts/new-order-actions-context-types';
import {
  Timeslot,
  TimeslotOption,
} from '@domain/fulfillment-settings/types/types';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useToasts } from '@/contexts/toast-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

export enum FulfillmentActionSheetRoute {
  Base = 'BASE',
  DaysView = 'DAYS_VIEW',
  TimeslotSelect = 'TIMESLOT_SELECT',
}

const FULFILLMENT_TYPE_TO_ROUTE_KEY_MAP: {
  [fulfillmentType in FulfillmentType]: FulfillmentActionSheetRoute | null;
} = {
  [FulfillmentType.FulfillmentDays]: FulfillmentActionSheetRoute.DaysView,
  [FulfillmentType.FulfillmentShippingOption]: null,
};

export type CombinedFulfillmentListOption = ListOption<{
  fulfillmentFee: string | null;
  fulfillmentOption: AvailableOrderFulfillmentFieldsFragment;
  nextRouteKey: FulfillmentActionSheetRoute | null;
}>;

interface Options {
  draftOrder: NewOrder_OrderFieldsFragment | null;
  fulfillmentOptions: AvailableOrderFulfillmentFieldsFragment[];
  initialSelectedFulfillment: SelectedFulfillmentType | null;
  initialSelectedFulfillmentOption?: AvailableOrderFulfillmentFieldsFragment;
  initialTimeslotOptions?: TimeslotOption[];
  onClose: () => void;
  onOpen?: () => void;
  onSave: (selectedFulfillment: SelectedFulfillmentType) => Promise<void>;
  placeOrderShortcutEnabled?: boolean;
}

export const useFulfillmentPicker = ({
  draftOrder,
  fulfillmentOptions,
  initialSelectedFulfillment,
  initialSelectedFulfillmentOption,
  initialTimeslotOptions,
  onClose,
  onOpen,
  onSave,
  placeOrderShortcutEnabled = true,
}: Options) => {
  const { currencyCode } = useVariantPackInfoContext();
  const { showErrorToast } = useToasts();

  const [selectedFulfillmentOption, setSelectedFulfillmentOption] =
    useState<AvailableOrderFulfillmentFieldsFragment | null>(
      initialSelectedFulfillmentOption ||
        (fulfillmentOptions.length === 1 ? fulfillmentOptions[0] : null),
    );
  const [savingFulfillment, setSavingFulfillment] =
    useState<SelectedFulfillmentType | null>(null);
  const [selectedFulfillmentDate, setSelectedFulfillmentDate] = useState(
    moment(initialSelectedFulfillment?.date),
  );
  const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | null>(
    initialSelectedFulfillment?.timeslot ?? null,
  );
  const [timeslotOptionsForDay, setTimeslotOptionsForDay] = useState<
    TimeslotOption[] | null
  >(initialTimeslotOptions || null);

  useEffect(() => {
    // Automatically select the first fulfillment option if it's the only one,
    // otherwise clear the selected one
    setSelectedFulfillmentOption((prev) => {
      return fulfillmentOptions.length === 1 ? fulfillmentOptions[0] : prev;
    });
  }, [fulfillmentOptions]);

  const saveFulfillment = useCallback(
    async (
      newSelectedFulfillment: SelectedFulfillmentType,
      finish: () => void,
    ) => {
      // Check if the fulfillment is already being saved
      if (savingFulfillment) {
        return;
      }

      if (
        initialSelectedFulfillment &&
        moment(newSelectedFulfillment.date).isSame(
          initialSelectedFulfillment.date,
        ) &&
        newSelectedFulfillment.type === initialSelectedFulfillment.type &&
        areTimeslotsEqual(
          newSelectedFulfillment.timeslot,
          initialSelectedFulfillment.timeslot,
        )
      ) {
        // Leave without saving if the fulfillment has not changed
        finish();
      } else {
        setSavingFulfillment(newSelectedFulfillment);
        try {
          await onSave(newSelectedFulfillment);
          finish();
        } catch (e) {
          showErrorToast();
        } finally {
          setSavingFulfillment(null);
        }
      }
    },
    [onSave, initialSelectedFulfillment, savingFulfillment, showErrorToast],
  );

  useKeyboardShortcuts(
    AllowedShortcuts.PlaceOrder,
    async () => {
      if (!initialSelectedFulfillment) {
        return;
      }
      // relies on saveFulfillment leaving the picker if fulfillment hasn't changed
      await saveFulfillment(initialSelectedFulfillment, onClose);
    },
    [],
    {},
    placeOrderShortcutEnabled,
    KeyboardScope.ACTION_SHEET,
  );

  useEffect(() => {
    onOpen?.();
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // wrap in callback to avoid re-rendering
  const clearTimeslot = useCallback(() => {
    setSelectedTimeslot(null);
  }, []);

  const fulfillmentFeeMap = useMemo(
    () =>
      (draftOrder?.order_fulfillment_fees || []).reduce<{
        [feeName: string]: string;
      }>(
        (map, fee) => ({
          ...map,
          [fee.fulfillment_settings_name as string]: formatCurrency(
            fee.amount_in_micros!,
            currencyCode,
          ),
        }),
        {},
      ),
    [currencyCode, draftOrder],
  );

  const listData = useMemo(() => {
    const fulfillmentNameSet = new Set<FulfillmentName>();
    return fulfillmentOptions.reduce<CombinedFulfillmentListOption[]>(
      (result, option) => {
        if (!fulfillmentNameSet.has(option.fulfillment_name)) {
          fulfillmentNameSet.add(option.fulfillment_name);
          result.push({
            metadata: {
              fulfillmentFee:
                fulfillmentFeeMap[option.fulfillment_name] || null,
              fulfillmentOption: option,
              nextRouteKey:
                FULFILLMENT_TYPE_TO_ROUTE_KEY_MAP[option.fulfillment_type] ||
                null,
            },
            title:
              option.fulfillment_display_name ||
              ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
                option.fulfillment_name
              ],
            value: option.fulfillment_name,
          });
        }

        return result;
      },
      [],
    );
  }, [fulfillmentFeeMap, fulfillmentOptions]);

  const defaultInitialRoute =
    fulfillmentOptions.length === 1 &&
    fulfillmentOptions[0].fulfillment_type === FulfillmentType.FulfillmentDays
      ? FulfillmentActionSheetRoute.DaysView
      : FulfillmentActionSheetRoute.Base;

  const resetSelection = useCallback(() => {
    setSelectedFulfillmentOption(
      fulfillmentOptions.length === 1 ? fulfillmentOptions[0] : null,
    );
    setSelectedTimeslot(initialSelectedFulfillment?.timeslot ?? null);
    setTimeslotOptionsForDay(null);
    setSelectedFulfillmentDate(moment(initialSelectedFulfillment?.date));
  }, [fulfillmentOptions, initialSelectedFulfillment]);

  const saveFulfillmentForToday = useCallback(
    (
      fulfillmentOption: AvailableOrderFulfillmentFieldsFragment,
      finish: () => void,
    ) => {
      saveFulfillment(
        {
          date: moment().startOf('day').toISOString(),
          fulfillmentSettingsUUID: fulfillmentOption.fulfillment_settings_id,
          timeslot: null,
          type: fulfillmentOption.fulfillment_name,
        },
        finish,
      );
    },
    [saveFulfillment],
  );

  const selectedFulfillmentLabel = initialSelectedFulfillment
    ? translateFulfillmentType(
        initialSelectedFulfillment.type,
        'fulfillment_header.type',
        fulfillmentOptions.find(
          (option) =>
            option.fulfillment_name === initialSelectedFulfillment.type,
        )?.fulfillment_display_name,
        {
          date: formatSummaryDate(new Date(initialSelectedFulfillment.date)),
        },
      )
    : '';

  const timeslotRouteTitle = i18n.t('select_time_slot_title', {
    fulfillmentName:
      selectedFulfillmentOption?.fulfillment_display_name ||
      i18n
        .t(
          selectedFulfillmentOption?.fulfillment_name || FulfillmentName.Pickup,
        )
        .toLowerCase(),
  });

  return {
    clearTimeslot,
    defaultInitialRoute,
    listData,
    resetSelection,
    saveFulfillment,
    saveFulfillmentForToday,
    savingFulfillment,
    selectedFulfillmentDate,
    selectedFulfillmentLabel,
    selectedFulfillmentOption,
    selectedTimeslot,
    setSelectedFulfillmentDate,
    setSelectedFulfillmentOption,
    setSelectedTimeslot,
    setTimeslotOptionsForDay,
    timeslotOptionsForDay,
    timeslotRouteTitle,
  };
};
